import { createSlice, isAnyOf } from '@reduxjs/toolkit';
import type { AppState } from '../../reducers/root.reducer';
import { createSelector } from 'reselect';
import IBaseState from '../../types/IBaseState';
import AdminInviteService from '../services/AdminInviteService';
import ProjectInvitation from '../types/invitation/ProjectInvitation';
import ProjectInvitationParticipant from '../types/invitation/ProjectInvitationParticipant';
import UploadState from '../../types/UploadState';
import ProjectInvitationRecap from '../types/invitation/ProjectInvitationRecap';
import { createNotifiedThunk } from '../../reducers/createAppAsyncThunk';

const NAME = 'AdminInvite';

const createInvite = createNotifiedThunk(
    `${NAME}/createInvite`,
    AdminInviteService.createInvite,
    { error: 'fe.admin.error.create-invitation' },
);

const getInvites = createNotifiedThunk(
    `${NAME}/getInvites`,
    AdminInviteService.getInvites,
    { error: 'fe.admin.error.fetch-invitations' },
);

const getInvite = createNotifiedThunk(
    `${NAME}/getInvite`,
    AdminInviteService.getInvite,
    { error: 'fe.admin.error.fetch-invitation' },
);
const saveInvite = createNotifiedThunk(
    `${NAME}/saveInvite`,
    AdminInviteService.saveInvite,
    { error: 'fe.admin.error.save-invitation' },
);
const getParticipants = createNotifiedThunk(
    `${NAME}/getParticipants`,
    AdminInviteService.getParticipants,
    { error: 'fe.admin.error.load-participants' },
);
const changeParticipantLanguage = createNotifiedThunk(
    `${NAME}/changeParticipantLanguage`,
    AdminInviteService.changeParticipantLanguage,
    { error: 'fe.admin.error.change-language' },
);
const selectParticipant = createNotifiedThunk(
    `${NAME}/selectParticipant`,
    AdminInviteService.selectParticipant,
    { error: 'fe.admin.error.change-language' },
);
const selectParticipants = createNotifiedThunk(
    `${NAME}/selectParticipants`,
    AdminInviteService.selectParticipants,
    { error: 'fe.admin.error.add-invitation-participants' },
);
const deselectParticipant = createNotifiedThunk(
    `${NAME}/deselectParticipant`,
    AdminInviteService.deselectParticipant,
    { error: 'fe.admin.error.remove-participant' },
);
const deselectParticipants = createNotifiedThunk(
    `${NAME}/deselectParticipants`,
    AdminInviteService.deselectParticipants,
    { error: 'fe.admin.error.remove-participants' },
);
const changeName = createNotifiedThunk(
    `${NAME}/changeName`,
    AdminInviteService.changeName,
    { error: 'fe.admin.error.save-invitation-name' },
);
const getRecap = createNotifiedThunk(
    `${NAME}/getRecaps`,
    AdminInviteService.getRecaps,
    { error: 'fe.admin.error.load-recaps' },
);
const setMultilingual = createNotifiedThunk(
    `${NAME}/setMultilingual`,
    AdminInviteService.setMultilingual,
    { error: 'fe.admin.error.save-invitation' },
);
const sendInvite = createNotifiedThunk(
    `${NAME}/sendInvite`,
    AdminInviteService.sendInvite,
    { error: 'fe.admin.error.send-invitation' },
);

export type AdminInviteState = {
    invites: IBaseState<ProjectInvitation[]>;
    detail: IBaseState<ProjectInvitation | null>;
    participants: UploadState<ProjectInvitationParticipant[] | null>;
    recap: IBaseState<ProjectInvitationRecap[]>;
};

const initialState: AdminInviteState = {
    invites: { data: [], loading: false },
    detail: { data: null, loading: false },
    participants: { data: null, loading: false, uploading: false },
    recap: { data: [] },
};

const slice = createSlice({
    name: NAME,
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(
            createInvite.fulfilled,
            (state: AdminInviteState, action) => {
                state.invites.data.push(
                    action.payload.data as ProjectInvitation,
                );
            },
        );

        builder.addCase(
            getParticipants.fulfilled,
            (state: AdminInviteState, action) => {
                state.participants.data = action.payload.data;
            },
        );

        builder.addCase(getInvites.pending, (state: AdminInviteState) => {
            state.invites.loading = true;
        });
        builder.addCase(
            getInvites.fulfilled,
            (state: AdminInviteState, action) => {
                state.invites.data = action.payload.data;
            },
        );

        builder.addCase(
            changeParticipantLanguage.fulfilled,
            (state: AdminInviteState, action) => {
                if (state.participants.data) {
                    const participantIndex = state.participants.data.findIndex(
                        (item: ProjectInvitationParticipant) =>
                            item.id === action.meta.arg.participantId,
                    );
                    if (participantIndex >= 0 && action.payload?.data?.locale) {
                        state.participants.data[participantIndex].locale =
                            action.payload.data.locale;
                    }
                }
            },
        );

        builder.addCase(
            deselectParticipant.fulfilled,
            (state: AdminInviteState, action) => {
                if (state.participants.data) {
                    const participantIndex = state.participants.data.findIndex(
                        (participant) =>
                            participant.id === action.meta.arg.participantId,
                    );

                    if (participantIndex >= 0) {
                        state.participants.data.splice(participantIndex, 1);
                    }
                }
            },
        );

        builder.addCase(
            deselectParticipants.fulfilled,
            (state: AdminInviteState) => {
                state.participants.data = [];
            },
        );

        builder.addCase(getRecap.pending, (state: AdminInviteState) => {
            state.recap.loading = true;
        });
        builder.addCase(
            getRecap.fulfilled,
            (state: AdminInviteState, action) => {
                state.recap.data = action.payload.data;
            },
        );

        builder.addMatcher(
            isAnyOf(
                selectParticipant.pending,
                selectParticipants.pending,
                deselectParticipant.pending,
                deselectParticipants.pending,
                changeParticipantLanguage.pending,
            ),
            (state: AdminInviteState) => {
                state.participants.uploading = true;
            },
        );
        builder.addMatcher(
            isAnyOf(
                selectParticipant.fulfilled,
                selectParticipant.rejected,
                selectParticipants.fulfilled,
                selectParticipants.rejected,
                deselectParticipant.fulfilled,
                deselectParticipant.rejected,
                deselectParticipants.fulfilled,
                deselectParticipants.rejected,
                changeParticipantLanguage.fulfilled,
                changeParticipantLanguage.rejected,
            ),
            (state: AdminInviteState) => {
                state.participants.uploading = false;
            },
        );

        builder.addMatcher(
            isAnyOf(selectParticipant.fulfilled, selectParticipants.fulfilled),
            (state: AdminInviteState, action) => {
                if (state.participants.data) {
                    state.participants.data.push(...action.payload.data);
                }
            },
        );

        builder.addMatcher(
            isAnyOf(getParticipants.pending),
            (state: AdminInviteState) => {
                state.participants.loading = true;
            },
        );
        builder.addMatcher(
            isAnyOf(getParticipants.fulfilled, getParticipants.rejected),
            (state: AdminInviteState) => {
                state.participants.loading = false;
            },
        );

        builder.addMatcher(
            isAnyOf(getInvites.fulfilled, getInvites.rejected),
            (state: AdminInviteState) => {
                state.invites.loading = false;
            },
        );

        builder.addMatcher(
            isAnyOf(getRecap.pending, sendInvite.pending),
            (state: AdminInviteState) => {
                state.recap.loading = false;
            },
        );
        builder.addMatcher(
            isAnyOf(
                getRecap.fulfilled,
                getRecap.rejected,
                sendInvite.fulfilled,
                sendInvite.rejected,
            ),
            (state: AdminInviteState) => {
                state.recap.loading = false;
            },
        );

        builder.addMatcher(
            isAnyOf(
                createInvite.pending,
                getInvite.pending,
                setMultilingual.pending,
            ),
            (state: AdminInviteState) => {
                state.detail.loading = true;
            },
        );
        builder.addMatcher(
            isAnyOf(
                createInvite.fulfilled,
                createInvite.rejected,
                getInvite.fulfilled,
                getInvite.rejected,
                setMultilingual.fulfilled,
                setMultilingual.rejected,
            ),
            (state: AdminInviteState) => {
                state.detail.loading = false;
            },
        );
        builder.addMatcher(
            isAnyOf(getInvite.fulfilled, setMultilingual.fulfilled),
            (state: AdminInviteState, action) => {
                state.detail.data = action.payload
                    ?.data as ProjectInvitation | null;
            },
        );
    },
});

const state = (state: AppState) => state.admin.invite;

const AdminInviteSlice = {
    reducer: slice.reducer,
    actions: {
        createInvite,
        getInvite,
        getInvites,
        getParticipants,
        selectParticipant,
        selectParticipants,
        deselectParticipant,
        deselectParticipants,
        changeParticipantLanguage,
        saveInvite,
        changeName,
        getRecap,
        setMultilingual,
        sendInvite,
    },
    state: {
        getAll: state,
        getDetail: createSelector(
            state,
            (state: AdminInviteState) => state.detail,
        ),
        getParticipants: createSelector(
            state,
            (state: AdminInviteState) => state.participants,
        ),
        getInvites: createSelector(
            state,
            (state: AdminInviteState) => state.invites,
        ),
        getRecap: createSelector(
            state,
            (state: AdminInviteState) => state.recap,
        ),
    },
};

export default AdminInviteSlice;
